import {
  createAsyncThunk,
  createEntityAdapter,
  createSelector,
  createSlice,
  EntityState,
  PayloadAction,
} from "@reduxjs/toolkit"
import { toast } from "react-toastify"

import { DataStatus, Notification, RootState, Team } from "@/types"
import { fetchMarkNotificationAsRead, fetchNotifications } from "@/utils"
import { handleStandardError } from "@/utils/errors"

import { clearDataOnLogout } from "./user"

const priorities = { high: 3, average: 2, low: 1 }

export const notificationsSortComparer = (a: Notification, b: Notification) => {
  if (priorities[a.priority] - priorities[b.priority] === 0) return a.created_at > b.created_at ? -1 : 1
  return priorities[a.priority] > priorities[b.priority] ? -1 : 1
}

const notificationsAdapter = createEntityAdapter<Notification>({
  sortComparer: notificationsSortComparer,
})

type NotificationsState = EntityState<Notification> & {
  status: DataStatus | "updating"
}

const initialState: NotificationsState = notificationsAdapter.getInitialState({
  status: "no_team",
})

export const getNotifications = createAsyncThunk(
  "notifications/getNotifications",
  async (teamsUuids: Array<Team["uuid"]>) => {
    const { response, json } = await fetchNotifications(teamsUuids)

    return {
      ok: response.ok,
      status: response.status,
      data: json.data,
    }
  }
)

export const requestMarkNotificationAsRead = createAsyncThunk(
  "notifications/requestMarkAsRead",
  async (id: Notification["id"], { dispatch }) => {
    const { response, json } = await fetchMarkNotificationAsRead(id)

    const updatedNotification = json?.data

    if (response.ok && updatedNotification) {
      dispatch(updateNotification(updatedNotification))

      toast.success("Oznaczono powiadomienie jako przeczytane.", {
        autoClose: 2000,
        updateId: "markNotificationAsRead",
        toastId: "markNotificationAsRead",
      })
    } else {
      handleStandardError(response, json)
    }

    return {
      ok: response.ok,
      status: response.status,
      data: updatedNotification,
    }
  }
)

const notificationsSlice = createSlice({
  name: "notifications",
  initialState,
  reducers: {
    updateNotification(state, action: PayloadAction<Notification>) {
      const updatedNotification = action.payload

      notificationsAdapter.upsertOne(state, updatedNotification)
    },
    updateNotifications(state, action: PayloadAction<Notification[]>) {
      const updatedNotifications = action.payload

      notificationsAdapter.upsertMany(state, updatedNotifications)
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getNotifications.pending, (state) => {
      if (state.ids.length) {
        state.status = "updating"
      } else {
        state.status = "loading"
      }
    })

    builder.addCase(getNotifications.fulfilled, (state, { payload }) => {
      if (payload.ok) {
        if (payload.data) notificationsAdapter.upsertMany(state, payload.data)
        state.status = "fetched"
      } else {
        state.status = "error"
      }
    })

    builder.addCase(clearDataOnLogout, () => initialState)
  },
})

const { selectAll, selectById } = notificationsAdapter.getSelectors((state: RootState) => state.notifications)

export const selectNotifications = (teamIds: Array<Team["id"]>) =>
  createSelector(selectAll, (notifications) =>
    notifications.filter(
      (notification) => notification.recipient_type === "Team" && teamIds.includes(notification.recipient_id)
    )
  )

export const selectNotificationsIds = (teamIds: Array<Team["id"]>) =>
  createSelector(selectNotifications(teamIds), (notifications) => notifications.map((notification) => notification.id))

export const selectNotificationById = (id: Notification["id"]) => (state: RootState) => selectById(state, id)

export const selectNotificationsStatus = (state: RootState) => state.notifications.status

export const { updateNotification, updateNotifications } = notificationsSlice.actions
export const notificationsReducer = notificationsSlice.reducer
